import React, { useEffect, useRef, useState, useCallback } from 'react';
import toast from 'react-hot-toast';

interface SelfieCaptureProps {
  onCapture: (file: File | null) => void;
  fotoPreviewUrl?: string;
  disabled?: boolean;
}

const SelfieCapture: React.FC<SelfieCaptureProps> = ({ onCapture, fotoPreviewUrl, disabled }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [cameraAtiva, setCameraAtiva] = useState(false);
  const [fotoCapturada, setFotoCapturada] = useState<string | null>(fotoPreviewUrl || null);
  const [erroCamera, setErroCamera] = useState('');
  const [carregandoCamera, setCarregandoCamera] = useState(false);

  const pararCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setCameraAtiva(false);
  }, []);

  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      }
    };
  }, []);

  const iniciarCamera = async () => {
    setErroCamera('');
    setCarregandoCamera(true);
    setCameraAtiva(true);

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user' }
      });
      streamRef.current = stream;

      await new Promise(resolve => setTimeout(resolve, 300));

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.onloadeddata = () => {
          console.log('[SelfieCapture] Video carregado:', videoRef.current?.videoWidth, 'x', videoRef.current?.videoHeight);
        };
        try {
          await videoRef.current.play();
          console.log('[SelfieCapture] Play iniciado com sucesso');
        } catch (playError) {
          console.error('[SelfieCapture] Erro no play:', playError);
        }
      } else {
        console.error('[SelfieCapture] videoRef.current é null!');
      }

      setCarregandoCamera(false);
    } catch (error) {
      console.error('[SelfieCapture] Erro ao acessar câmera:', error);
      setErroCamera('Não foi possível acessar a câmera. Verifique as permissões do navegador.');
      toast.error('Não foi possível acessar a câmera.');
      setCarregandoCamera(false);
      setCameraAtiva(false);
    }
  };

  const capturarFoto = () => {
    if (!videoRef.current || !canvasRef.current) {
      toast.error('Câmera não está disponível.');
      return;
    }
    
    const video = videoRef.current;
    const canvas = canvasRef.current;
    const largura = video.videoWidth;
    const altura = video.videoHeight;
    
    if (largura === 0 || altura === 0) {
      toast.error('Câmera não está pronta. Tente novamente.');
      return;
    }
    
    canvas.width = largura;
    canvas.height = altura;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    ctx.drawImage(video, 0, 0, largura, altura);

    canvas.toBlob((blob) => {
      if (!blob) return;
      const file = new File([blob], `selfie_${Date.now()}.jpg`, { type: 'image/jpeg' });
      const url = URL.createObjectURL(blob);
      setFotoCapturada(url);
      onCapture(file);
      pararCamera();
      toast.success('Foto capturada com sucesso!');
    }, 'image/jpeg', 0.9);
  };

  const tirarNovamente = () => {
    setFotoCapturada(null);
    onCapture(null);
    iniciarCamera();
  };

  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-700">
        Foto do Visitante (selfie 3x4) <span className="text-red-500">*</span>
      </label>

      {fotoCapturada ? (
        <div className="flex items-center gap-4">
          <img
            src={fotoCapturada}
            alt="Selfie do visitante"
            className="w-28 h-36 object-cover rounded-lg border border-gray-300"
          />
          {!disabled && (
            <button
              type="button"
              onClick={tirarNovamente}
              className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
            >
              Tirar novamente
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            className={`w-full max-w-xs rounded-lg border border-gray-300 bg-black aspect-[3/4] object-cover ${cameraAtiva ? 'block' : 'hidden'}`}
          />
          {cameraAtiva && (
            <div className="flex gap-2">
              <button
                type="button"
                onClick={capturarFoto}
                disabled={carregandoCamera}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {carregandoCamera ? 'Aguardando...' : 'Capturar Foto'}
              </button>
              <button
                type="button"
                onClick={pararCamera}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Cancelar
              </button>
            </div>
          )}
          {!cameraAtiva && (
            <button
              type="button"
              onClick={iniciarCamera}
              disabled={disabled || carregandoCamera}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-blue-700 bg-blue-50 rounded-lg border border-blue-200 hover:bg-blue-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {carregandoCamera ? (
                <>
                  <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-blue-700"></div>
                  Ativando câmera...
                </>
              ) : (
                <>
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  Ativar câmera e tirar selfie
                </>
              )}
            </button>
          )}
          {erroCamera && <p className="text-sm text-red-600">{erroCamera}</p>}
        </div>
      )}

      <canvas ref={canvasRef} className="hidden" />
    </div>
  );
};

export default SelfieCapture;
